import React, { useState, useEffect, useRef } from 'react'
import './organizationSelector.scss'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import store from '../../redux/store'
import { useAuth0 } from '../../contexts/auth0-context'
import { DropdownMenu } from './components/DropdownMenu'
import { CurrentOrganizationLogo } from './components/CurrentOrganizationLogo'
import { Organization, OrganizationDropdownProps } from './types'

const OrganizationSelector: React.FC<OrganizationDropdownProps> = ({ organization, isDarkMode, isAdmin }) => {
    const { getTokenSilently } = useAuth0()
    const [organizations, setOrganizations] = useState<Organization[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [isPrivacyShield, setIsPrivacyShield] = useState(true)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Fetch organizations when dropdown opens
    useEffect(() => {
        if (isOpen) {
            fetchOrganizations()
            document.addEventListener('mousedown', handleClickOutside)
        } else {
            setIsPrivacyShield(true)
            document.removeEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false)
        }
    }

    const fetchOrganizations = async () => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            let orgs = await callAPI(endpoints.getAllOrganizations.url, endpoints.getAllOrganizations.method, token)

            // Sort organizations: favorites first, then alphabetically by name
            orgs = sortOrganizations(orgs)
            setOrganizations(orgs)
        } catch (error) {
            console.error('Error fetching organizations:', error)
        }
    }

    const sortOrganizations = (orgs: Organization[]): Organization[] => {
        return orgs.sort((a, b) => {
            // First sort by favorite status
            if (a.isFavorite && !b.isFavorite) return -1
            if (!a.isFavorite && b.isFavorite) return 1
            // Then sort alphabetically by name
            return a.name.localeCompare(b.name)
        })
    }

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const removePrivacyShield = () => {
        setIsPrivacyShield(false)
    }

    const handleOrganizationSelect = async (orgId: string) => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            const userRecord = store.getState().userRecord
            const updatedUser = {
                ...userRecord,
                organizationId: orgId,
            }

            await callAPI(`${endpoints.updateUser.url}/org/${orgId}`, endpoints.updateUser.method, token, updatedUser)

            store.dispatch({ type: 'SET_USER', payload: updatedUser })
            store.dispatch({ type: 'CLEAR_STRATEGY' })
            setIsOpen(false)

            // Full document reload rather than history.push. The app-wide QueryClient is
            // in-memory only, so reloading is what drops the previous org's cached data —
            // most query keys have no organizationId dimension, and a soft navigation
            // leaves them serving the previous org for the rest of their cacheTime.
            window.location.assign('/roadmaps')
        } catch (error) {
            console.error('Error updating user organization:', error)
        }
    }

    return isAdmin ? (
        <div data-testid="organization-selector" className="organization-dropdown" ref={dropdownRef}>
            <CurrentOrganizationLogo organization={organization} isDarkMode={isDarkMode} onClick={toggleDropdown} />

            {isOpen && (
                <DropdownMenu
                    organizations={organizations}
                    isDarkMode={isDarkMode}
                    isPrivacyShieldOpen={isPrivacyShield}
                    onRemovePrivacyShield={removePrivacyShield}
                    onOrganizationSelect={handleOrganizationSelect}
                />
            )}
        </div>
    ) : null
}

export default OrganizationSelector
